import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import i18n from 'i18next';
import { Button } from 'modules/Core/Common/Button';
import { Toast } from 'modules/Core/Common/Toast';
import { Title } from 'modules/Core/Common/Title';
import { defaultErrorToast } from 'modules/Utils';
import { Async } from 'modules/Auth/AuthSlice';
import { TwoFactorModal } from 'modules/Auth/TwoFactor';
import * as S from './SidebarTwoFactor.styles';

const SidebarTwoFactor = ({ user }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [pendingMethod, setPendingMethod] = useState(null);

  const isEnabled = !!user?.otp_enabled;
  const availableMethods = user?.two_factor_available_methods || ['totp'];

  const handleDisable = () => {
    setLoading(true);
    dispatch(
      Async.profileDisable2FA({
        onSuccess: () => {
          setLoading(false);
          Toast(
            i18n.t(
              'modules.settings-profile.forms.two-factor.disabled-success'
            ),
            'success'
          );
        },
        onError: (e) => {
          setLoading(false);
          defaultErrorToast(
            i18n.t('errors.error-sorry-an-error-occurred-during.default')
          )(e);
        },
      })
    );
  };

  const handleSetupTotp = () => {
    setLoading(true);
    dispatch(
      Async.profileGetSetup2FA({
        onSuccess: (data) => {
          setLoading(false);
          setSetupData(data);
          setPendingMethod('totp');
          setShowSetupModal(true);
        },
        onError: (e) => {
          setLoading(false);
          defaultErrorToast(
            i18n.t('errors.error-sorry-an-error-occurred-during.default')
          )(e);
        },
      })
    );
  };

  const handleSetupEmail = () => {
    setPendingMethod('email');
    setSetupData({});
    setShowSetupModal(true);
  };

  const handleConfirmSetup = (code, method) => {
    if (method === 'email') {
      dispatch(
        Async.profileVerifyEmail2FA({
          code,
          onSuccess: () => {
            setShowSetupModal(false);
            setSetupData(null);
            setPendingMethod(null);
            Toast(
              i18n.t(
                'modules.settings-profile.forms.two-factor.enabled-success'
              ),
              'success'
            );
          },
          onError: (e) => {
            defaultErrorToast(
              i18n.t('errors.error-sorry-an-error-occurred-during.default')
            )(e);
          },
        })
      );
      return;
    }

    dispatch(
      Async.profileConfirmSetup2FA({
        code,
        onSuccess: () => {
          setShowSetupModal(false);
          setSetupData(null);
          Toast(
            i18n.t('modules.settings-profile.forms.two-factor.enabled-success'),
            'success'
          );
        },
        onError: (e) => {
          defaultErrorToast(
            i18n.t('errors.error-sorry-an-error-occurred-during.default')
          )(e);
        },
      })
    );
  };

  return (
    <div>
      <Title tag="h4" size="quartenary">
        {i18n.t('modules.settings-profile.forms.two-factor.title')}
      </Title>

      {isEnabled ? (
        <>
          <S.Status color="success">
            {i18n.t('modules.settings-profile.forms.two-factor.status-enabled')}
          </S.Status>
          <Button
            danger
            medium
            onClick={handleDisable}
            hasLoading={loading}
            loading={loading}
            disabled={loading}
          >
            {i18n.t('modules.settings-profile.forms.two-factor.disable-button')}
          </Button>
        </>
      ) : (
        <>
          <S.Status color="text_base">
            {i18n.t(
              'modules.settings-profile.forms.two-factor.status-disabled'
            )}
          </S.Status>
          {availableMethods.includes('totp') && (
            <Button
              medium
              onClick={handleSetupTotp}
              hasLoading={loading}
              loading={loading}
              disabled={loading}
            >
              {i18n.t('modules.settings-profile.forms.two-factor.setup-button')}
            </Button>
          )}
          {availableMethods.includes('email') && (
            <Button
              medium
              outlined
              onClick={handleSetupEmail}
              disabled={loading}
            >
              {i18n.t(
                'modules.settings-profile.forms.two-factor.setup-email-button'
              )}
            </Button>
          )}
        </>
      )}

      <TwoFactorModal
        isOpen={showSetupModal && !!setupData}
        mode="setup"
        forcedMethod={pendingMethod}
        methodOptions={availableMethods}
        setupData={setupData}
        onConfirm={handleConfirmSetup}
        onClose={() => {
          setShowSetupModal(false);
          setSetupData(null);
          setPendingMethod(null);
        }}
      />
    </div>
  );
};

SidebarTwoFactor.propTypes = {
  user: PropTypes.object,
};

export default SidebarTwoFactor;
